import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Play } from 'lucide-react';
import { SITE, SOCIALS } from '../mock';
import { fetchFeaturedClip } from '../hooks/useStreamStatus';
import { useReveal } from '../hooks/useReveal';

const cleanTitle = (t) => (t || '').replace(/([!?$])\1+/g, '$1').replace(/[$]+$/g, '').trim();

function ClipPlayer({ clip }) {
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!playing || !ref.current) return undefined;
    const v = ref.current;
    let hls;
    if (v.canPlayType('application/vnd.apple.mpegurl')) v.src = clip.videoUrl;
    else if (Hls.isSupported()) { hls = new Hls(); hls.loadSource(clip.videoUrl); hls.attachMedia(v); }
    v.play().catch(() => {});
    return () => hls && hls.destroy();
  }, [playing, clip.videoUrl]);

  return (
    <figure className="m-0">
      <div className="relative aspect-video rounded-xl overflow-hidden border border-[var(--ar-border)] bg-black">
        {playing ? (
          <video ref={ref} controls playsInline className="w-full h-full" data-testid="about-clip-video" aria-label={cleanTitle(clip.title)} />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} data-testid="about-clip-play-btn" aria-label={`Play clip: ${cleanTitle(clip.title)}`} className="group absolute inset-0 w-full h-full">
            <img src={clip.thumbnail} alt="" className="w-full h-full object-cover" />
            <span className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full flex items-center justify-center bg-[var(--ar-cyan)] text-[#05222b] transition-transform group-hover:scale-110"><Play size={28} fill="currentColor" aria-hidden="true" /></span>
            </span>
          </button>
        )}
      </div>
      <figcaption className="mt-2 text-xs text-muted flex flex-wrap justify-between gap-2">
        <span>Clip: <span className="text-[var(--ar-text)]">{cleanTitle(clip.title)}</span> · captions not available</span>
        <a href={clip.url} target="_blank" rel="noopener noreferrer" className="text-cyan hover:underline" data-testid="about-clip-kick-link">View on Kick</a>
      </figcaption>
    </figure>
  );
}

export default function About() {
  useReveal();
  const [clip, setClip] = useState(null);
  useEffect(() => { fetchFeaturedClip().then((c) => c.available && setClip(c)).catch(() => {}); }, []);

  return (
    <section id="about" className="pb-12 sm:pb-16 relative" data-testid="about-section">
      <div className="ar-container">
        <div className={`ar-panel p-6 sm:p-9 grid gap-8 items-center reveal ${clip ? 'lg:grid-cols-2' : ''}`}>
          <div>
            <p className="ar-eyebrow mb-3">About</p>
            <h2 className="ar-heading text-2xl sm:text-3xl mb-4">Who Is <span className="text-cyan">AsianRisk</span>?</h2>
            <p className="text-muted leading-relaxed mb-4">
              AsianRisk is a Kick streamer in the Slots &amp; Casino category and a Roobet affiliate using code {SITE.code}. Streams follow Roobet sessions live, with chat reacting to every bonus along the way.
            </p>
            <ul className="text-sm text-muted space-y-2 mb-6">
              <li><span className="text-[var(--ar-text)] font-semibold">Live on Kick</span> — casino sessions with chat</li>
              <li><span className="text-[var(--ar-text)] font-semibold">Monthly leaderboard</span> — $500 total prize pool for the top five</li>
              <li><span className="text-[var(--ar-text)] font-semibold">Discord community</span> — event and giveaway announcements</li>
            </ul>
            <a href={SOCIALS.kick} target="_blank" rel="noopener noreferrer" data-testid="about-follow-kick-btn" className="btn-gold">FOLLOW ON KICK</a>
          </div>
          {clip && <ClipPlayer clip={clip} />}
        </div>
      </div>
    </section>
  );
}
